import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Copy, Check, Download } from "lucide-react";
import { getMyInvestor, getMyReferrals, getReferralPdfUrl, type InvestorReferral } from "@/lib/investor.functions";


export const Route = createFileRoute("/investor/")({
  head: () => ({
    meta: [
      { title: "Referrals - Investor - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InvestorHome,
});

function InvestorHome() {
  const runGetInvestor = useServerFn(getMyInvestor);
  const runGetReferrals = useServerFn(getMyReferrals);
  const runGetPdf = useServerFn(getReferralPdfUrl);
  const [investor, setInvestor] = useState<{ id: string; name: string; ref_code: string | null; active: boolean } | null>(null);
  const [referrals, setReferrals] = useState<InvestorReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [notRegistered, setNotRegistered] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ investor }, { referrals }] = await Promise.all([runGetInvestor(), runGetReferrals()]);
        if (!investor) setNotRegistered(true);
        else setInvestor(investor as { id: string; name: string; ref_code: string | null; active: boolean });
        setReferrals(referrals);
      } finally {
        setLoading(false);
      }
    })();
  }, [runGetInvestor, runGetReferrals]);

  const refLink = investor?.ref_code
    ? `https://deckscore.ai/?ref=${investor.ref_code}`
    : "";

  async function copyLink() {
    if (!refLink) return;
    try {
      await navigator.clipboard.writeText(refLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* non-fatal */ }
  }

  async function downloadPdf(id: string) {
    try {
      const { url } = await runGetPdf({ data: { referralId: id } });
      if (url) window.open(url, "_blank", "noopener");
    } catch (err) {
      console.error(err);
      alert("Couldn't fetch that one-pager.");
    }
  }

  if (loading) return <p className="text-ink-secondary">Loading…</p>;
  if (notRegistered) {
    return (
      <div className="bg-surface border border-line rounded-2xl p-10 text-center">
        <h1 className="font-display text-[24px] font-semibold">Register as an investor</h1>
        <p className="text-ink-secondary mt-2">Complete a one-time profile to get your DeckScore page.</p>
        <a href="/for-investors" className="inline-flex mt-4 rounded-full bg-accent text-accent-ink px-5 py-2 text-[14px] font-bold">
          Continue →
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="mb-8">
        <p className="font-script text-accent text-[22px] leading-none">investor</p>
        <h1 className="font-display text-[28px] font-semibold mt-1">{investor?.name}</h1>
        {investor && !investor.active && (
          <p className="text-[13px] text-ink-secondary mt-1">
            Pending admin review - referrals still work today. You'll be added to
            paid submission targeting once approved.
          </p>
        )}
      </div>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8 mb-8">
        <h2 className="font-display text-[18px] font-semibold mb-2">Your dealflow page</h2>
        <p className="text-ink-secondary text-[13.5px] mb-3">
          Share this with founders. Each analysis via your dealflow page sends
          you a free investor one-pager - no obligation to invest.
        </p>
        <div className="flex gap-2 items-center">
          <input
            readOnly
            value={refLink}
            className="flex-1 min-h-[40px] rounded-lg border border-line bg-bg px-3 font-mono text-[13px]"
          />
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-ink px-4 py-2 text-[13px] font-semibold"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </section>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8">
        <h2 className="font-display text-[18px] font-semibold mb-4">Referred decks</h2>
        {referrals.length === 0 ? (
          <p className="text-ink-secondary text-[14px]">
            No referrals yet. Share your dealflow page to get your first one-pager.
          </p>
        ) : (
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left text-ink-tertiary text-[12px] uppercase tracking-wide border-b border-line">
                <th className="py-2 pr-4 font-medium">Company</th>
                <th className="py-2 pr-4 font-medium">Delivered</th>
                <th className="py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id} className="border-b border-line/60 last:border-0">
                  <td className="py-3 pr-4">
                    <Link
                      to="/investor/referrals/$deckId"
                      params={{ deckId: r.deck_id }}
                      className="text-ink hover:text-accent hover:underline"
                    >
                      {r.company}
                    </Link>
                  </td>
                  <td className="py-3 pr-4 text-ink-secondary">{new Date(r.delivered_at).toLocaleDateString()}</td>
                  <td className="py-3">
                    <div className="flex items-center gap-4">
                      <Link
                        to="/investor/referrals/$deckId"
                        params={{ deckId: r.deck_id }}
                        className="text-accent hover:underline text-[13.5px]"
                      >
                        View deck & analysis →
                      </Link>
                      {r.one_pager_pdf_path ? (
                        <button
                          onClick={() => downloadPdf(r.id)}
                          className="inline-flex items-center gap-1.5 text-ink-secondary hover:text-ink text-[13.5px]"
                        >
                          <Download className="w-3.5 h-3.5" /> One-pager
                        </button>
                      ) : (
                        <span className="text-ink-tertiary text-[13px]">Preparing…</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
